import { useState } from "react";

export const landmarks = [
  { name: "Tampa International Airport (TPA)", icon: "ri-flight-takeoff-line", distance: "~5 miles from downtown Tampa", desc: "Major international airport serving the Tampa Bay region with extensive domestic and international routes. Hub for Southwest Airlines with nonstop service to Europe, the Caribbean, and Latin America. Also serves Port Tampa Bay cruise passengers.", url: "https://www.tampaairport.com/", schemaType: "Airport" },
  { name: "Port Tampa Bay", icon: "ri-ship-line", distance: "Downtown Tampa — Channelside District", desc: "Florida's largest port by tonnage and a major cruise hub. Carnival, Royal Caribbean, and Norwegian cruise lines operate year-round Caribbean itineraries — all passengers require valid passports for international cruise travel.", url: "https://www.porttb.com/", schemaType: "TouristAttraction" },
  { name: "St. Pete–Clearwater Intl Airport (PIE)", icon: "ri-flight-takeoff-line", distance: "~20 miles west — Clearwater", desc: "Secondary airport serving Allegiant Air with select domestic routes. Popular alternative for Pinellas County travelers seeking budget-friendly flights to secondary markets.", url: "https://www.fly2pie.com/", schemaType: "Airport" },
  { name: "Busch Gardens Tampa Bay", icon: "ri-landscape-line", distance: "~8 miles north of downtown", desc: "African-themed amusement park with world-class roller coasters, live entertainment, and one of North America's largest zoos. A major tourism driver for the Tampa Bay region drawing millions of visitors annually.", url: "https://buschgardens.com/tampa/", schemaType: "TouristAttraction" },
  { name: "Ybor City Historic District", icon: "ri-store-2-line", distance: "~2 miles northeast of downtown", desc: "National Historic Landmark District known for its Cuban and cigar-making heritage. Now a vibrant entertainment district with restaurants, nightlife, and cultural attractions — a defining landmark of Tampa's multicultural identity.", url: "https://www.yborcityonline.com/", schemaType: "TouristAttraction" },
  { name: "Tampa Riverwalk", icon: "ri-landscape-line", distance: "Downtown Tampa — along Hillsborough River", desc: "2.6-mile waterfront path connecting parks, museums, restaurants, and hotels along the Hillsborough River. A central recreational and cultural corridor linking Tampa's downtown attractions.", url: "https://www.tampagov.net/", schemaType: "TouristAttraction" },
  { name: "Raymond James Stadium", icon: "ri-building-line", distance: "~5 miles west of downtown", desc: "Home of the Tampa Bay Buccaneers and host of major events including Super Bowls, college football championships, and international soccer matches. A major sports landmark drawing visitors nationwide.", url: "https://raymondjamesstadium.com/", schemaType: "TouristAttraction" },
  { name: "The Florida Aquarium", icon: "ri-anchor-line", distance: "Channelside District — Downtown Tampa", desc: "200,000-square-foot aquarium featuring Florida wetlands, coral reefs, and marine life exhibits. A major family attraction and conservation center in downtown Tampa's Channelside district.", url: "https://www.flaquarium.org/", schemaType: "TouristAttraction" },
];

const photoLocations = [
  { name: "CVS Pharmacy — N Dale Mabry Hwy", address: "4012 N Dale Mabry Hwy, Tampa, FL 33607", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — S Dale Mabry Hwy", address: "3802 S Dale Mabry Hwy, Tampa, FL 33611", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — E Hillsborough Ave (24 hrs)", address: "3802 E Hillsborough Ave, Tampa, FL 33610", hours: "Open 24 Hours" },
  { name: "Walgreens — W Kennedy Blvd", address: "3302 W Kennedy Blvd, Tampa, FL 33609", hours: "Call for hours" },
  { name: "Walgreens — N Armenia Ave (24 hrs)", address: "4002 N Armenia Ave, Tampa, FL 33607", hours: "Open 24 Hours" },
  { name: "Walgreens — S MacDill Ave", address: "3802 S MacDill Ave, Tampa, FL 33611", hours: "Call for hours" },
  { name: "The UPS Store — N Dale Mabry Hwy", address: "4012 N Dale Mabry Hwy, Ste 101, Tampa, FL 33607", hours: "Call for hours" },
  { name: "The UPS Store — W Kennedy Blvd", address: "3302 W Kennedy Blvd, Ste 103, Tampa, FL 33609", hours: "Call for hours" },
  { name: "FedEx Office — N Dale Mabry Hwy", address: "4012 N Dale Mabry Hwy, Tampa, FL 33607", hours: "Call for hours" },
  { name: "FedEx Office — E Fowler Ave", address: "13001 E Fowler Ave, Tampa, FL 33612", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Hillsborough County Tax Collector — Brandon",
    address: "3030 N Falkenburg Rd, Tampa, FL 33619",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "Driver licenses, REAL ID, titles & registration. Appointment recommended.",
    featured: true,
  },
  {
    name: "Hillsborough County Tax Collector — North Tampa",
    address: "3011 University Center Dr, Tampa, FL 33612",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "North Tampa option. Call ahead for current wait times.",
    featured: false,
  },
  {
    name: "Hillsborough County Tax Collector — South Tampa",
    address: "4100 W Dr Martin Luther King Jr Blvd, Tampa, FL 33614",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "South Tampa option. Appointment recommended.",
    featured: false,
  },
];

export default function TampaHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Tampa Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Tampa resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">

          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <h3 className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations in Tampa</h3>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular passport photo locations in Tampa include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Hillsborough County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Hillsborough County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Hillsborough County. Online, mail, or in-person options available.", url: "https://hillsborough.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Hillsborough County. Required for estate and legal matters.", url: "https://hillsborough.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Hillsborough County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.hillsclerk.com/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Hillsborough County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.hillsclerk.com/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Hillsborough County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <h3 className="text-white font-bold text-base leading-tight mt-0.5">Hillsborough County Tax Collector — DMV Services</h3>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Florida driver&apos;s license or REAL ID, the Hillsborough County Tax Collector handles DMV services in the Tampa area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.hillstax.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Hillsborough County Tax Collector — DMV Services
                </a>
                <a href="https://www.flhsmv.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>FLHSMV — Florida DMV
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online at the Hillsborough County Tax Collector website before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <h3 className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Tampa Travelers</h3>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Tampa Bay is served by three main airports. If your trip is coming up fast, make sure your passport is in hand well before your departure date.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {[
                  {
                    code: "TPA",
                    name: "Tampa International Airport",
                    distance: "Primary airport — 5 miles from downtown Tampa",
                    address: "4100 George J Bean Pkwy, Tampa, FL 33607",
                    note: "Major hub for American, Delta, Southwest, United & international carriers. Also serves Port Tampa Bay cruise passengers.",
                    flightUrl: "https://www.tampaairport.com/",
                    flightLabel: "TPA Airport Website",
                    featured: true,
                  },
                  {
                    code: "PIE",
                    name: "St. Pete–Clearwater International Airport",
                    distance: "~20 miles west of Tampa",
                    address: "14700 Terminal Blvd, Clearwater, FL 33762",
                    note: "Serves select domestic routes including Allegiant Air. Good alternative for Pinellas County travelers.",
                    flightUrl: "https://www.fly2pie.com/",
                    flightLabel: "PIE Airport Website",
                    featured: false,
                  },
                  {
                    code: "SRQ",
                    name: "Sarasota Bradenton International Airport",
                    distance: "~55 miles south of Tampa",
                    address: "6000 Airport Cir, Sarasota, FL 34243",
                    note: "Serves Sarasota, Bradenton, and Manatee County travelers with domestic and limited international flights.",
                    flightUrl: "https://srq-airport.com/",
                    flightLabel: "SRQ Airport Website",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights and most cruises.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

          {/* Nearby Landmarks */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-landscape-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Nearby Landmarks</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Key Landmarks &amp; Transportation Hubs Near Tampa</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Tampa Bay is defined by its waterfront — from the bustling Port Tampa Bay cruise terminal to the scenic Riverwalk. These landmarks and transportation hubs shape how Tampa residents travel, whether departing on a Caribbean cruise, flying out of TPA, or visiting one of the region's world-class attractions.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                {landmarks.map((landmark, i) => (
                  <a
                    key={i}
                    href={landmark.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 group-hover:bg-[#F6C344]/15 transition-colors">
                        <i className={`${landmark.icon} text-[#1A365D] text-sm`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{landmark.name}</p>
                        <p className="text-gray-400 text-[11px]">{landmark.distance}</p>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">{landmark.desc}</p>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international travel.</strong> Whether you are departing from TPA, boarding a cruise from Port Tampa Bay, or flying out of PIE — a valid U.S. passport is mandatory. <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">Expedited processing through Fast Passport Center</a> is the fastest option for Tampa Bay residents.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}